import React, { useEffect, useRef, useState } from 'react';
import Button from '../ds/components/Button';
import CuantoLibera from './CuantoLibera';
import { recomendar } from '../data/planes';
import OfertaProducto from './OfertaProducto';
import { ofertaPara, queEsPara, AGENDADOR } from '../data/ofertas';
import { escenaPara } from '../data/escenas';
import DemoAgencia from './DemoAgencia';
import AgenciaCelular from './AgenciaCelular';
import CatalogoAgencia from './CatalogoAgencia';
import CatalogoEntrada from './CatalogoEntrada';
import BrujulaGuia from './BrujulaGuia';
import { evento } from '../lib/medicion';
import { PRODUCTOS } from '../data/productos';

// 🟠 Texto del aviso: provisorio hasta que An lo defina (ver ESPECIFICACION.md §9).
export const VERSION_AVISO = 'provisorio-2026-10-18';

export const textoPlazo = (a) => {
  if (!a || !a.plazo) return 'Te escribiremos a la brevedad.';
  const dias = `${a.plazo} ${a.plazo === 1 ? 'día hábil' : 'días hábiles'}`;
  if (a.fuera_hasta) {
    const f = new Date(`${a.fuera_hasta}T12:00:00`);
    if (!Number.isNaN(f.getTime()) && f > new Date()) {
      return `Estamos fuera hasta el ${f.toLocaleDateString('es-AR', { day: 'numeric', month: 'long' })}. Te escribimos a partir de ese día, dentro de ${dias}.`;
    }
  }
  return `Te escribiremos dentro de ${dias}.`;
};

// La altura de lectura del scroll que cuenta: el medio de la pantalla, o en el teléfono (celular arriba) el medio de lo
// que queda debajo del celular.
const lineaDeLectura = (recorrido) => {
  const cel = recorrido?.querySelector('.gh-cel');
  const apilado = window.matchMedia('(max-width: 900px)').matches;
  const arriba = apilado && cel ? Math.max(cel.getBoundingClientRect().bottom, 0) : 0;
  return (arriba + window.innerHeight) / 2;
};

// La página final (spec v4): primero el valor (el prompt o su tablero), después la oferta apilada del
// producto por el que entró, con un solo botón al contacto. El rumbo para compartir queda al pie.
const CierreHome = ({ node, collected, onNavigate, onRestart, abrirChat }) => {
  const { plan: sugerido, motivo, nivel } = recomendar(collected);
  const oferta = ofertaPara(collected?.producto, { nivel, habilidad: collected?.habilidad, personal: collected?.para === 'personal' });
  const escena = escenaPara(collected?.producto, collected);
  const queEs = queEsPara(collected?.producto, collected);
  // La calculadora va donde el producto libera tiempo; el Scan es un diagnóstico y no lo promete (revisión 05/11).
  const conPrueba = ['ia', 'habilidades', 'agentes'].includes(collected?.producto);
  const [ajustes, setAjustes] = useState(undefined);
  const [verMas, setVerMas] = useState(false);
  // Las tres partes de Agencia cambian la pantalla del celular. El scroll que cuenta (04/11): el celular queda fijo y
  // muestra la parte que está a la altura de lectura (el medio de la pantalla, o el medio de lo que queda debajo del
  // celular en el teléfono). Con movimiento reducido quedan las pestañas: cada una cambia la pantalla al tocarla.
  const [paso, setPaso] = useState(0);
  const [travesia, setTravesia] = useState(false);
  const recorridoRef = useRef(null);
  const pasosRef = useRef(null);
  useEffect(() => {
    if (!queEs?.celular || !window.matchMedia) return undefined;
    const quieto = window.matchMedia('(prefers-reduced-motion: reduce)');
    const ver = () => setTravesia(!quieto.matches);
    ver();
    quieto.addEventListener?.('change', ver);
    return () => quieto.removeEventListener?.('change', ver);
  }, [queEs?.celular]);
  useEffect(() => {
    if (!travesia) return undefined;
    let cuadro = 0;
    const medir = () => {
      cuadro = 0;
      const lis = [...(pasosRef.current?.children || [])];
      const y = lineaDeLectura(recorridoRef.current);
      let cerca = 0;
      let dist = Infinity;
      lis.forEach((li, i) => {
        const r = li.getBoundingClientRect();
        const d = Math.abs(r.top + r.height / 2 - y);
        if (d < dist) { dist = d; cerca = i; }
      });
      setPaso(cerca);
    };
    const pedir = () => { if (!cuadro) cuadro = requestAnimationFrame(medir); };
    medir();
    window.addEventListener('scroll', pedir, { passive: true });
    window.addEventListener('resize', pedir);
    return () => { window.removeEventListener('scroll', pedir); window.removeEventListener('resize', pedir); cancelAnimationFrame(cuadro); };
  }, [travesia]);
  // Una frase tocada (o el link «registro» del celular) lleva a su pantalla; en la travesía, además, la frase va a la
  // altura de lectura, para que el scroll y el celular sigan diciendo lo mismo.
  const irA = (i) => {
    setPaso(i);
    const li = travesia && pasosRef.current?.children[i];
    if (!li) return;
    const r = li.getBoundingClientRect();
    window.scrollTo({ top: window.scrollY + r.top + r.height / 2 - lineaDeLectura(recorridoRef.current), behavior: 'smooth' });
  };
  const pantalla = node.id;

  useEffect(() => {
    evento('resultado_visto', { pantalla, plan: sugerido });
    evento('valor_visto', { pantalla });
    let vivo = true;
    fetch('/.netlify/functions/ajustes').then(r => r.json()).then(a => { if (vivo) setAjustes(a); }).catch(() => { if (vivo) setAjustes(null); });
    return () => { vivo = false; };
  }, [pantalla, sugerido]);


  // Postularse abre el chat de la esquina con el asunto escrito y los datos del recorrido, que viajan con el
  // contacto (An, 04/11: «tiene que abrirse en el diálogo con el asunto de inscripción»; un solo formulario).
  // Las preguntas del producto van ahí, para quien ya leyó y se postula (An, 05/11); el chat saltea las contestadas.
  const postularse = () => {
    evento('contacto_abierto', { pantalla, plan: sugerido });
    const { businessName, rubroSugerido, ...resto } = collected || {};
    const nombre = oferta ? oferta.titulo.replace(' · ', ' (') + (oferta.titulo.includes(' · ') ? ')' : '') : 'GUIA';
    abrirChat?.(
      oferta?.cohorte ? `Quiero mi lugar en la cohorte fundadora de ${nombre}.` : `Quiero postularme a ${nombre}.`,
      {
        asunto: oferta?.cohorte ? 'Inscripción a la cohorte fundadora' : `Postulación a ${oferta ? oferta.titulo.split(' · ')[0] : 'GUIA'}`,
        servicio: node.servicio || node.id,
        respuestas: { ...resto, negocio: businessName || '', nivel: nivel || '' },
        plan_sugerido: sugerido, plan_motivo: motivo,
        preguntas: PRODUCTOS.find(p => p.id === collected?.producto)?.preguntas || [],
      },
    );
  };

  const pausada = ajustes && ajustes.pausada;

  // El botón de decidir aparece temprano, no solo al final (evaluación de la UI, 04/11): lleva a la oferta.
  const irAOferta = (e) => { e.preventDefault(); document.getElementById('gh-oferta')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); };
  const atajo = oferta && !oferta.formatos && (
    <a href="#gh-oferta" className="guia-btn guia-btn--solido gh-atajo" onClick={irAOferta}>{oferta.boton || 'Quiero empezar'}</a>
  );

  const contacto = pausada ? (
    <p className="gh-nota">Por ahora no estamos recibiendo consultas por acá.{ajustes.mensaje ? ` ${ajustes.mensaje}` : ''}</p>
  ) : (
    // Dos salidas (An, 04/11): postularse y esperar el mensaje de An, o agendar una llamada para sacarse dudas.
    <div className="gh-acciones gh-salidas">
      <Button variant="solido" onClick={postularse}>{oferta?.boton || 'Quiero postularme'}</Button>
      <a className="gh-salidas__llamada" href={AGENDADOR} target="_blank" rel="noopener noreferrer" onClick={() => evento('escribime_tocado', { pantalla, plan: sugerido, opcion: 'agendar' })}>¿Tenés dudas? Agendá una llamada de 20 minutos</a>
    </div>
  );

  // La página es una landing de tres bandas a todo el ancho, cada una con una sola cosa dominante (An, 04/11:
  // «sigue siendo un fondo blanco lleno de letras»): el producto andando · cuánto vale, en un campo Ciruela ·
  // la oferta. Lo que no aplica al producto no se muestra.
  return (
    <div className="gh-cierre">
      {node.navTo ? (
        <section className="gh-banda" aria-labelledby="gh-c-valor">
          <div className="gh-banda__in gh-cierre__bloque">
            <h2 id="gh-c-valor" className="gh-cierre__titulo">Mirá cómo quedaría</h2>
            <div className="gh-puntos">{(node.bullets || []).map(b => <div key={b} className="gh-punto">{b}</div>)}</div>
            {node.priceNote && <p className="gh-nota">{node.priceNote}</p>}
            <div className="gh-acciones"><Button onClick={onNavigate}>Quiero verlo →</Button></div>
          </div>
        </section>
      ) : (<>
        {queEs && (
          // Lo que cambia con el producto, antes de verlo andando: vende el resultado (An, 04/11).
          <section className="gh-banda gh-banda--quees" aria-labelledby="gh-c-quees">
            <div className={`gh-banda__in${queEs.celular ? ' gh-quees--celular' : ''}${queEs.catalogo ? ' gh-quees--entrada' : ''}${travesia ? ' gh-quees--travesia' : ''}`}>
              <div className="gh-quees__recorrido" ref={recorridoRef}>
                {queEs.celular && <AgenciaCelular paso={paso} onIr={irA} />}
                <div className="gh-quees__texto-col">
                  {queEs.volanta && <p className="gh-volanta">{queEs.volanta}</p>}
                  <div className="gh-guia"><BrujulaGuia /><h2 id="gh-c-quees" className="gh-quees__titulo">{queEs.titulo}</h2></div>
                  {queEs.texto && <p className="gh-quees__texto">{queEs.texto}</p>}
                  {queEs.celular ? (
                    <ol className="gh-quees__partes gh-quees__pasos" role="tablist" aria-label="Qué hace Agencia" ref={pasosRef}>
                      {queEs.partes.map(([t], i) => (
                        <li key={t}>
                          <button type="button" role="tab" aria-selected={paso === i} onClick={() => irA(i)}>
                            <span className="gh-quees__num">{String(i + 1).padStart(2, '0')}</span>{t}
                          </button>
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <ol className="gh-quees__partes">
                      {queEs.partes.map(([t, d]) => <li key={t}><h3>{t}</h3>{d && <p>{d}</p>}</li>)}
                    </ol>
                  )}
                </div>
                {queEs.catalogo && <CatalogoEntrada {...queEs.catalogo} origen={collected?.producto} />}
              </div>
              {queEs.mas && (
                <div className="gh-quees__mas-fila">
                  <button type="button" className="gh-quees__mas" aria-expanded={verMas} aria-controls="gh-quees-mas" onClick={() => setVerMas(v => !v)}>
                    {queEs.masTitulo}<i className="gh-abre" aria-hidden="true" />
                  </button>
                  {verMas && queEs.masIntro && <p className="gh-quees__intro">{queEs.masIntro}</p>}
                  {verMas && (
                    <dl className="gh-quees__detalle" id="gh-quees-mas">
                      {queEs.mas.map(([t, d]) => <div key={t}><dt>{t}</dt><dd>{d}</dd></div>)}
                    </dl>
                  )}
                </div>
              )}
            </div>
          </section>
        )}
        {queEs && <div className="gh-trama-banda" aria-hidden="true" />}
        {/* El catálogo (An, 05/11): en Agencia, las tareas que ya sabe hacer, por agente, con su video y su demo */}
        {queEs?.celular && (
          <section className="gh-banda gh-banda--catalogo" aria-labelledby="gh-c-catalogo">
            <div className="gh-banda__in">
              <div className="gh-guia"><BrujulaGuia /><h2 id="gh-c-catalogo" className="gh-cierre__titulo">Las tareas de tu agencia</h2></div>
              <p className="gh-catalogo__bajada">Un agente por área. Tocá una tarea y mirá cómo la hace.</p>
              <CatalogoAgencia abrirChat={abrirChat} datos={{ producto: collected?.producto, para: collected?.para || '' }} />
            </div>
          </section>
        )}
        {/* Con el celular, la ventana de la demo repetía lo mismo: en Agencia queda solo el celular (04/11, «sábana») */}
        {escena && !queEs?.celular && (
          <section className="gh-banda gh-banda--demo" aria-labelledby="gh-c-ejecuta">
            <div className="gh-banda__in gh-ejecuta">
              <div className="gh-ejecuta__texto">
                <p className="gh-volanta">Así se ve</p>
                <h2 id="gh-c-ejecuta" className="gh-cierre__titulo">Un pedido, de punta a punta.</h2>
                <p>Tocá Aprobar y mirá cómo queda en el registro de tareas.</p>
              </div>
              <DemoAgencia key={escena.pedido} escena={escena} />
            </div>
          </section>
        )}
        {conPrueba && (
          <section className="gh-banda gh-banda--medi" aria-labelledby="gh-c-valor">
            <div className="gh-banda__in">
              <div className="gh-guia"><BrujulaGuia /><h2 id="gh-c-valor" className="gh-cierre__titulo">«¿Cuánto me puede ayudar a mí?»</h2></div>
              <CuantoLibera collected={collected} producto={oferta ? oferta.titulo.split(' · ')[0] : null} />
              {atajo && <div className="gh-atajo__fila">{atajo}</div>}
            </div>
          </section>
        )}
        {/* La misma trama Orquídea que la primera banda: una familia de color por pantalla (revisión 05/11) */}
        {conPrueba && <div className="gh-trama-banda" aria-hidden="true" />}
      </>)}

      <section className="gh-banda gh-banda--oferta" id="gh-oferta" aria-label="La oferta">
        <div className="gh-banda__in">
          {oferta ? (
            <OfertaProducto oferta={oferta} nivel={nivel} equipo={collected?.equipo} boton={contacto} />
          ) : (
            <div className="gh-cierre__bloque">
              <h2 id="gh-c-cierre" className="gh-cierre__titulo">Sigamos hablando</h2>
              {contacto}
            </div>
          )}
          <button onClick={onRestart} className="gh-sutil">← Ver todos los productos</button>
        </div>
      </section>
    </div>
  );
};

export default CierreHome;
